/**
 *
 * LargeCalendar
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import BigCalendar from 'components/interaction/BigCalendar/index.js';
import useDialog from 'containers/Dialog/useDialog.js';
import doNothing from 'utils/doNothing.js';
import tinyFunction, { limitLuminosity } from 'utils/tinyFunction.js';
import { useFixedBottomStyle } from 'utils/fixedBottomStyle.js';
import Toolbar from './Toolbar.jsx';
import messages from './messages.js';
import getLockedMessage from './getLockedMessage.js';
import useStudentColor from './useStudentColor.js';

function toolbarWithBlocks(props) {
  return <Toolbar {...props} />;
}

const BigWrapper = styled.div`
  height: ${(p) => 107 + (40 * p.hours)}px;
  .rbc-time-header {
    display: none;
  }
  .rbc-event {
    width: calc(${(p) => 100 / p.cols}% - 10px) !important;
    left: 0 !important;
  }
`;

function LargeCalendar(props) {
  const { meetingId, student, hours, events, min, max, defaultDate, dates, students } = props;
  const intl = useIntl();
  const navigate = useNavigate();
  const { dlgAlert } = useDialog();
  const getColor = useStudentColor();
  const [ref, bottomStyle] = useFixedBottomStyle(window.socsIsInAppWebView ? 112 : 400);

  function selectEvent(info) {
    const lockedMessage = getLockedMessage(info.set, info.student.id);
    if (lockedMessage) {
      dlgAlert({ message: lockedMessage });
      return;
    }
    const { id, meeting } = info;
    navigate(`/students/${meeting.studentId}/meetings/${meeting.details.set.id}#block=${id}`);
  }

  function titleAccessor(event) {
    return (
      <div>
        {event.set.title}
        <div className="font-weight-bold">
          {event.slotsAvailable}
          {' '}
          <FormattedMessage {...messages.options} />
        </div>
      </div>
    );
  }

  function tooltipAccessor(event) {
    const lockedMessage = getLockedMessage(event.set, event.student.id);
    if (lockedMessage) {
      return intl.formatMessage(lockedMessage);
    }
    return `${event.set.title} (${event.slotsAvailable} ${intl.formatMessage(messages.options)})`;
  }

  function eventPropGetter(event) {
    const width = 100 / students.length;
    const active = event.end.getTime() > Date.now() && event.slotsAvailable > 0 && event.set.id === meetingId && event.student.id === student.userId;
    const color = getColor(event.student);
    const backgroundColor = active ? color : limitLuminosity(tinyFunction('lighten')(color, 20));
    const style = {
      marginLeft: `calc(${event.offset * (width)}% + 5px)`,
      backgroundColor,
      borderColor: '#fff',
    };
    return { className: `offset-${event.offset}`, style };
  }

  return (
    <BigWrapper ref={ref} style={bottomStyle} hours={hours} cols={students.length}>
      <BigCalendar
        events={events}
        min={min}
        max={max}
        defaultDate={defaultDate}
        view="day"
        onSelectEvent={selectEvent}
        eventPropGetter={eventPropGetter}
        titleAccessor={titleAccessor}
        tooltipAccessor={tooltipAccessor}
        onView={doNothing}
        components={{ toolbar: (p) => toolbarWithBlocks({ ...p, dates, students }) }}
      />
    </BigWrapper>
  );
}

LargeCalendar.propTypes = {
  meetingId: PropTypes.number,
  student: PropTypes.object,
  hours: PropTypes.number,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  events: PropTypes.array,
  dates: PropTypes.array,
  defaultDate: PropTypes.instanceOf(Date),
  students: PropTypes.array,
};

export default LargeCalendar;
